import React, { useState } from "react";
import { capitalize } from "lodash";
import { Segment, Header, Button, Checkbox } from "semantic-ui-react";
import { toast } from "react-semantic-toasts";
import { format } from "date-fns";
import { useDispatch } from "react-redux";

import {
    revokeInvitationEmail,
    updateNotificationPreferences,
    updateUserPermission,
} from "../actions/list";
import { DateTimeFormat } from "utilities/dist/invoice/constants";
import { CUSTOMER_USER_LIST_ROW_UPDATE } from "../constants";
import { copyInvitationLink, getRegisteredTime } from "../helpers";
import config from "utilities/dist/config";
import { buildTabcId } from "utilities/dist/customer/helpers";

const NotificationPreferences = ({
    app_notification,
    email_notification,
    sms_notification,
    handleNotificationPreferenceUpdate,
}) => {
    const [preferences, setPreferences] = useState({
        app_notification,
        email_notification,
        sms_notification,
    });

    return (
        <div>
            {["app_notification", "email_notification", "sms_notification"].map(key => {
                return (
                    <div key={key}>
                        <Checkbox
                            label={capitalize(key.replace("_", " "))}
                            checked={!!preferences[key]}
                            onChange={(_, { checked }) =>
                                setPreferences(data => ({ ...data, [key]: checked }))
                            }
                        />
                    </div>
                );
            })}

            <br />
            <Button
                icon="bell"
                content={"Update preferences"}
                onClick={() => handleNotificationPreferenceUpdate(preferences)}
            />
        </div>
    );
};

const SingleUserComponent = ({ setSelectedUser, user: customerUser, company, customerData }) => {
    const dispatch = useDispatch();
    const isEnable = customerUser.is_enable;
    const isPending = customerUser.is_pending;
    const isRevoked = customerUser.revoked;
    const userLogginTime = customerUser?.userActivity?.map(item => item.created_at);

    const isAlreadyRegistered = getRegisteredTime(customerUser);

    const handlePermission = permission => {
        updateUserPermission({ permission, customerUser, dispatch }).then(res => {
            toast({
                description: `You have updated access successfully`,
                time: 2000,
                type: "success",
                icon: "checkmark",
                title: "User Access",
            });
            setSelectedUser(res.data);
            dispatch({
                type: CUSTOMER_USER_LIST_ROW_UPDATE,
                payload: res.data,
            });
        });
    };

    const handleNotificationPreferenceUpdate = (preferences = {}) => {
        updateNotificationPreferences({ customerUser, preferences })
            .then(res => {
                toast({
                    description: `Notification preferences for this user has been updated`,
                    time: 10000,
                    type: "success",
                    icon: "checkmark",
                    title: "Preference Updated",
                });
                setSelectedUser(res.data);
                dispatch({
                    type: CUSTOMER_USER_LIST_ROW_UPDATE,
                    payload: res.data,
                });
            })
            .catch(() => {
                toast({
                    description: `Error updating notification preferences`,
                    time: 10000,
                    type: "error",
                    icon: "bell",
                    title: "Update failed",
                });
            });
    };

    const handleRevoke = () => {
        revokeInvitationEmail({ revoke: !isRevoked, customerUser, dispatch }).then(res => {
            toast({
                description: `You have revoked access for this email`,
                time: 2000,
                type: "success",
                icon: "checkmark",
                title: "Revoked",
            });
            setSelectedUser(res.data);
            dispatch({
                type: CUSTOMER_USER_LIST_ROW_UPDATE,
                payload: res.data,
            });
        });
    };
    const handleReactivate = () => {
        revokeInvitationEmail({ revoke: !isRevoked, customerUser, dispatch }).then(res => {
            toast({
                description: `You have re-activated the access for this email`,
                time: 2000,
                type: "success",
                icon: "checkmark",
                title: "Re-activate",
            });
            setSelectedUser(res.data);
            dispatch({
                type: CUSTOMER_USER_LIST_ROW_UPDATE,
                payload: res.data,
            });
        });
    };
    const copyInvitation = () =>
        copyInvitationLink({
            protocol: config.protocol,
            subDomain: config.order_app_subdomain,
            tabcId: buildTabcId(customerData),
            domain: config.client_host,
            companyId: company.id,
            ...customerUser,
        });

    return (
        <>
            <Segment attached="top">
                <div>
                    <p>
                        <b>{customerUser.name}</b>
                        <br />
                        {customerUser.email}
                        <br />
                        {customerUser.phone_number}
                    </p>
                </div>

                <Header
                    data-cy="notification_preference"
                    size="small"
                    content="Notification Preference"
                    dividing
                />
                <NotificationPreferences
                    key={customerUser.id}
                    {...customerUser}
                    handleNotificationPreferenceUpdate={handleNotificationPreferenceUpdate}
                />

                <Header data-cy="user_activity" size="small" content="User Activity" dividing />
                <p>
                    Last Signed In at:{" "}
                    {customerUser?.userActivity?.length
                        ? format(new Date(userLogginTime), DateTimeFormat)
                        : "Never Signed In"}
                </p>
            </Segment>
            <Button.Group attached="bottom">
                <ActionButtons
                    isPending={isPending}
                    isEnable={isEnable}
                    isAlreadyRegistered={isAlreadyRegistered}
                    handlePermission={handlePermission}
                    isRevoked={isRevoked}
                    handleRevoke={handleRevoke}
                    handleReactivate={handleReactivate}
                    copyInvitation={copyInvitation}
                />
            </Button.Group>
        </>
    );
};

const ActionButtons = ({
    isEnable,
    isPending,
    isRevoked,
    isAlreadyRegistered,
    handlePermission,
    handleRevoke,
    handleReactivate,
    copyInvitation,
}) => {
    if (isPending) {
        return (
            <Button
                icon="user times"
                attached="bottom"
                content={"Approve Access"}
                data-cy="approve_access"
                onClick={() => handlePermission("approve")}
            />
        );
    }
    if (!!isEnable || !!isRevoked) {
        return (
            <Button
                icon="user times"
                content={isAlreadyRegistered ? "Disable access" : "Re-activate"}
                data-cy={isAlreadyRegistered ? "disable_access" : "reactivate_access"}
                attached="bottom"
                onClick={isAlreadyRegistered ? () => handlePermission("disable") : handleReactivate}
            />
        );
    }

    return (
        <>
            <Button
                icon={isAlreadyRegistered ? "user plus" : "cancel"}
                color={isAlreadyRegistered ? "green" : "gray"}
                attached="bottom"
                data-cy={isAlreadyRegistered ? "enable_access" : "revoke_access"}
                content={isAlreadyRegistered ? "Enable access" : "Revoke"}
                onClick={isAlreadyRegistered ? () => handlePermission("enable") : handleRevoke}
            />
            {!isAlreadyRegistered && (
                <Button
                    color="blue"
                    icon="copy"
                    attached="bottom"
                    content="Copy Invite Link"
                    data-cy="copy_invite_link"
                    onClick={() => copyInvitation()}
                />
            )}
        </>
    );
};

export default SingleUserComponent;
